import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { apiStatusCode, EnvVariables, StorageKey } from './helper.type';
import { getEnvironmentVariable } from '../helper/config';
import { AuthService } from '../service/authService';
import { RefreshAccessTokenResponse } from '@inplay/models';

/**
 * Custom config to track retry status
 */
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Flag to avoid multiple simultaneous refresh attempts
let isRefreshing = false;

// Queue of failed requests waiting for refreshed token
type FailedRequest = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let failedQueue: FailedRequest[] = [];

/**
 * Process queued requests after token refresh
 */
function processQueue(error: unknown, token: string | null = null) {
  console.log(
    `[Axios][processQueue] Processing ${failedQueue.length} queued request(s)...`,
  );
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
}

/**
 * Refreshes access token
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  console.log('[refreshAccessToken] Token refresh initiated...');

  const isRefreshingMainAccessToken: boolean = Boolean(
    localStorage.getItem(StorageKey.ACCESS_TOKEN),
  );

  const refreshToken: string | null = isRefreshingMainAccessToken
    ? localStorage.getItem(StorageKey.REFRESH_TOKEN)
    : localStorage.getItem(StorageKey.STAFF_REFRESH_TOKEN);

  try {
    if (!refreshToken) {
      console.info('[refreshAccessToken] No refresh token found.');
      throw new Error('Missing refresh token');
    }

    const authService: AuthService = new AuthService();
    const refreshTokenResponse: AxiosResponse<RefreshAccessTokenResponse> =
      await authService.refreshToken(refreshToken);
    const newAccessToken: string = refreshTokenResponse?.data?.accessToken;

    if (isRefreshingMainAccessToken) {
      localStorage.setItem(StorageKey.ACCESS_TOKEN, newAccessToken);
    } else {
      localStorage.setItem(StorageKey.STAFF_ACCESS_TOKEN, newAccessToken);
    }

    processQueue(null, newAccessToken);
    return newAccessToken;
  } catch (error: unknown) {
    console.error('[refreshAccessToken] Refresh failed', error);
    processQueue(error, null);

    // Only remove the token that was used
    if (isRefreshingMainAccessToken) {
      console.warn('[refreshAccessToken] Removing USER tokens');
      localStorage.removeItem(StorageKey.ACCESS_TOKEN);
      localStorage.removeItem(StorageKey.REFRESH_TOKEN);
      localStorage.removeItem(StorageKey.VENUE_UNIT_DETAILS);
    } else {
      console.warn('[refreshAccessToken] Removing STAFF tokens');
      localStorage.removeItem(StorageKey.STAFF_ACCESS_TOKEN);
      localStorage.removeItem(StorageKey.STAFF_REFRESH_TOKEN);
    }

    // Optionally redirect or bubble up
    window.location.href = '/login';
    throw error;
  } finally {
    isRefreshing = false;
  }
};

class AxiosClient {
  private axiosInstance;

  constructor() {
    // Get the base URL from environment variables
    const BASE_URL: string = getEnvironmentVariable(EnvVariables.BASE_URL);

    // Create an Axios instance
    this.axiosInstance = axios.create({
      baseURL: BASE_URL,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
    });

    /**
     * Request Interceptor
     * Attaches access token to each outgoing request if available.
     */
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Only set Authorization header if not already set
        if (!config.headers['Authorization']) {
          const staffToken: string | null =
            localStorage.getItem(StorageKey.STAFF_ACCESS_TOKEN) ||
            sessionStorage.getItem(StorageKey.STAFF_ACCESS_TOKEN);

          const userToken: string | null =
            localStorage.getItem(StorageKey.ACCESS_TOKEN) ||
            sessionStorage.getItem(StorageKey.ACCESS_TOKEN);

          const finalToken: string | null = userToken || staffToken;

          if (finalToken) {
            config.headers['Authorization'] = `Bearer ${finalToken}`;
          }
        }

        console.log(
          '[Axios][Request]',
          config.method?.toUpperCase(),
          config.url,
        );
        return config;
      },
      (error: AxiosError) => {
        console.error('[Axios][Request Error]', error);
        return Promise.reject(error);
      },
    );

    /**
     * Response Interceptor
     * - Updates token if new one is returned in headers
     * - Handles token refresh if a 502 (Internal Server) is received
     */
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // If new token is sent in response header, update local storage
        if (response.headers['authorization']) {
          const newToken = response.headers['authorization'].split(' ')[1];
          const originalAuthHeader =
            response.config.headers?.Authorization ||
            response.config.headers?.authorization;
          const originalToken =
            typeof originalAuthHeader === 'string'
              ? originalAuthHeader.split(' ')[1]
              : null;

          const staffToken = localStorage.getItem(
            StorageKey.STAFF_ACCESS_TOKEN,
          );

          if (originalToken === staffToken) {
            localStorage.setItem(StorageKey.STAFF_ACCESS_TOKEN, newToken);
            console.log('[Axios][Staff Token Updated]', newToken);
          } else {
            localStorage.setItem(StorageKey.ACCESS_TOKEN, newToken);
            console.log('[Axios][User Token Updated]', newToken);
          }
          console.log('[Axios][Token Updated]', newToken);
        }

        console.log('[Axios][Response]', response.config.url, response.status);
        return response.data;
      },
      async (error: unknown) => {
        const originalRequest: CustomAxiosRequestConfig = (error as AxiosError)
          .config as CustomAxiosRequestConfig;

        // Only handle specific status codes and avoid infinite loop
        if (
          error instanceof AxiosError &&
          error.response?.data?.statusCode ===
            apiStatusCode.EXPIRED_ACCESS_TOKEN &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          if (isRefreshing) {
            // Queue the failed request while token is refreshing
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(this.axiosInstance(originalRequest));
                },
                reject,
              });
            });
          }

          try {
            const newToken: string = await refreshAccessToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return this.axiosInstance(originalRequest);
          } catch (err) {
            // Handle 419: Refresh token failure → redirect handled, no need to show toast

            if (
              error instanceof AxiosError &&
              error.response?.data?.statusCode ===
                apiStatusCode.EXPIRED_ACCESS_TOKEN
            ) {
              return Promise.reject({
                ...(error as AxiosError),
                _skipErrorToast: true,
              });
            }
            return Promise.reject(error);
          }
        }

        console.error('[Axios][Response Error]', error);
        return Promise.reject(error);
      },
    );
  }

  /**
   * Returns the configured Axios instance
   */
  public getAxiosInstance() {
    return this.axiosInstance;
  }
}

// Export a single instance of the client
const axiosClient = new AxiosClient();
export default axiosClient.getAxiosInstance();
